import { ColDef, ICellRendererParams } from 'ag-grid-community';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTrucksQuery } from '../../../hooks/queries/useTrucksQuery';
import { useAuth } from '../../../hooks/useAuth';
import { capitalizeFirstLetter, formatName } from '../../../utils/capitalize';
import { getPriceTypeInfos, localStorageKeys, permissionLevels, systemConfigKeyEnum } from '../../../utils/enums';
import { formatNumber } from '../../../utils/numbers';
import { InheritedPriceDisplay } from '../../InheritedPriceDisplay';
import LicensePlate from '../../LicensePlate';
import GenericAgGridTable from '../GenericAgGridTable';
import { Truck } from '../../../services/apiSLTP/materials/trucks';
import { useSystemConfig } from '../../../hooks/useSystemConfig';

interface TrucksPLITableProps {}

const TrucksPLITable: React.FC<TrucksPLITableProps> = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const {getConfigValue} = useSystemConfig();

    const { data: dataTrucks, isLoading: isTrucksLoading } = useTrucksQuery({ queries: { active: true } });

    const sortedTrucks = () => {
        return (
            dataTrucks
                ?.filter((truck) => truck.truckCompanyId.isOwnCompany)
                .sort((a, b) => {
                    const typeA = a.truckTypeId?.displayOrder || 0;
                    const typeB = b.truckTypeId?.displayOrder || 0;
                    if (typeA < typeB) return -1;
                    if (typeA > typeB) return 1;
                    return a.designation.localeCompare(b.designation);
                }) || []
        );
    };

    const columnDefs: ColDef<Truck>[] = [
        {
            headerName: capitalizeFirstLetter(t('name')),
            field: 'designation',
            initialFlex: 1,
            minWidth: 400,
            colId: 'name',
        },
        {
            headerName: capitalizeFirstLetter(t('price') + ` (${getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)})`),
            valueGetter: (params) => params.data?.price ?? params.data?.truckTypeId?.price,
            type: 'numericColumn',
            filter: 'agNumberColumnFilter',
            cellRenderer: (params: ICellRendererParams<Truck>) =>
                params.data ? (
                    <InheritedPriceDisplay
                        directValue={params.data.price}
                        inheritedValue={params.data.truckTypeId?.price}
                        format={(value) => formatNumber(value, t('locale'))}
                    />
                ) : params.value,
            colId: 'price',
        },
        {
            headerName: capitalizeFirstLetter(t('pricing')),
            valueGetter: (params) => (params.data ? t(getPriceTypeInfos(params.data.priceType ?? params.data.truckTypeId?.priceType)) : ''),
            cellRenderer: (params: ICellRendererParams<Truck>) =>
                params.data ? (
                    <InheritedPriceDisplay
                        directValue={params.data.priceType}
                        inheritedValue={params.data.truckTypeId?.priceType}
                        format={(value) => capitalizeFirstLetter(t(getPriceTypeInfos(value)))}
                    />
                ) : params.value,
            colId: 'pricing',
        },
        {
            headerName: capitalizeFirstLetter(t('codeProC')),
            field: 'codeProC',
            colId: 'codeProC',
        },
        {
            headerName: capitalizeFirstLetter(t('type')),
            field: 'truckTypeId.designation',
            colId: 'type',
        },
        {
            headerName: capitalizeFirstLetter(t('plateNumber')),
            field: 'plateNumber',
            minWidth: 150,
            cellRenderer: (params: ICellRendererParams<Truck>) => (params.data?.plateNumber ? <LicensePlate number={params.data.plateNumber} /> : params.value),
            colId: 'plateNumber',
        },
        {
            headerName: capitalizeFirstLetter(t('driver')),
            valueGetter: (params) => (params.data ? formatName(params.data.userId?.firstName, params.data.userId?.lastName) : ''),
            colId: 'driver',
        },
        ...((user?.profileId.permsLevel === permissionLevels.ADMIN
            ? [
                  {
                      headerName: capitalizeFirstLetter(t('_id')),
                      field: '_id',
                      cellClass: 'usage-col-main',
                      headerClass: 'usage-col-main',
                      initialWidth: 250,
                      colId: '_id',
                  },
              ]
            : []) as ColDef<Truck>[]),
    ];

    return (
        <GenericAgGridTable
            rowData={sortedTrucks()}
            columnDefs={columnDefs}
            loading={isTrucksLoading}
            exportCsvFileName="sales-pli-trucks"
            storageKey={localStorageKeys.TABLE.PLI.TRUCKS}
            agGridProps={{ getRowId: ({ data }) => data._id }}
        />
    );
};

export default TrucksPLITable;
